// ---------------------------------------------------------------------------
// usePlanogramEditor
//
// Thin React glue around PlanogramEditor. This hook owns ONE piece of
// state — `editor: PlanogramEditor` — plus the "origin" snapshot used by
// useUnsavedChangesGuard, and exposes every PlanogramEditor mutation as a
// stable callback that:
//   1. computes the next editor via the (pure, synchronous) domain method
//   2. setState's it
//   3. flips the dirty flag (delegated to the caller via onMutate, so this
//      hook doesn't need to know about useUnsavedChangesGuard directly)
//
// IMPORTANT: this hook intentionally contains NO business logic of its own.
// If you're tempted to add an `if` that decides *what* a mutation does,
// that belongs in PlanogramEditor/PlanogramGridOps, not here. This file's
// only job is "React state lives here, and these are the action creators."
// ---------------------------------------------------------------------------

import { useCallback, useRef, useState } from 'react';
import { PlanogramEditor } from '../domain/PlanogramEditor';
import { PlanogramData, ProductOption, CellKey } from '../domain/types';

export interface UsePlanogramEditorOptions {
    /** Called after every successful mutation — wire this to useUnsavedChangesGuard's markDirty(). */
    onMutate?: () => void;
    /** Called when a bounded operation (addColumn/addRow) hits its limit. */
    onLimitError?: (kind: 'columnsRange' | 'rowsRange') => void;
    /** Called when mergeSelected rejects a non-contiguous/multi-row selection. */
    onMergeError?: (kind: 'invalidSelection') => void;
}

export function usePlanogramEditor(initial: PlanogramEditor | null, options: UsePlanogramEditorOptions = {}) {
    const [editor, setEditor] = useState<PlanogramEditor | null>(initial);
    const originRef = useRef<PlanogramEditor | null>(initial?.snapshot() ?? null);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const notifyMutated = useCallback(() => optionsRef.current.onMutate?.(), []);

    /** Loads a freshly-fetched or freshly-created editor and resets the dirty baseline. Use after initial fetch AND after a successful save. */
    const load = useCallback((next: PlanogramEditor) => {
        setEditor(next);
        originRef.current = next.snapshot();
    }, []);

    const resetToOrigin = useCallback(() => {
        if (originRef.current) setEditor(originRef.current.snapshot());
    }, []);

    // -------------------------------------------------------------------
    // Generic apply-and-mark-dirty wrapper for the common case (mutation
    // always succeeds, just needs the dirty flag flipped).
    // -------------------------------------------------------------------
    const apply = useCallback((mutate: (e: PlanogramEditor) => PlanogramEditor) => {
        setEditor(prev => {
            if (!prev) return prev;
            const next = mutate(prev);
            if (next !== prev) notifyMutated();
            return next;
        });
    }, [notifyMutated]);

    // -------------------------------------------------------------------
    // Cell-level
    // -------------------------------------------------------------------

    const updateCellField = useCallback(
        (
            rowIndex: number,
            colIndex: number,
            field: string,
            value: unknown,
        ) => {
            apply(editor =>
                editor.updateCellField(
                    rowIndex,
                    colIndex,
                    field,
                    value,
                ),
            );
        },
        [apply],
    );

    const deleteCell = useCallback((rowIndex: number, colIndex: number) => {
        apply(e => e.deleteCell(rowIndex, colIndex));
    }, [apply]);

    const clearCell = useCallback((rowIndex: number, colIndex: number) => {
        apply(e => e.clearCell(rowIndex, colIndex));
    }, [apply]);

    const clearCells = useCallback((keys: CellKey[]) => {
        apply(e => e.clearCells(keys));
    }, [apply]);

    const duplicateCell = useCallback((sourceRowIndex: number, sourceColIndex: number, targetRowIndex: number, targetColIndex: number) => {
        apply(e => e.duplicateCell(sourceRowIndex, sourceColIndex, targetRowIndex, targetColIndex));
    }, [apply]);

    // -------------------------------------------------------------------
    // Product assignment
    // -------------------------------------------------------------------

    const addProduct = useCallback((rowIndex: number, colIndex: number, productOption: ProductOption) => {
        apply(e => e.addProduct(rowIndex, colIndex, productOption));
    }, [apply]);

    const patchProductDetails = useCallback((rowIndex: number, colIndex: number, fullProduct: ProductOption) => {
        apply(e => e.patchProductDetails(rowIndex, colIndex, fullProduct));
    }, [apply]);

    const applyBulkCellSettings = useCallback((
        keys: CellKey[],
        buildCell: Parameters<PlanogramEditor['applyBulkCellSettings']>[1],
    ) => {
        apply(e => e.applyBulkCellSettings(keys, buildCell));
    }, [apply]);

    const applySingleCellSettings = useCallback((
        rowIndex: number,
        colIndex: number,
        buildCell: Parameters<PlanogramEditor['applySingleCellSettings']>[2],
    ) => {
        apply(e => e.applySingleCellSettings(rowIndex, colIndex, buildCell));
    }, [apply]);

    const applyPriceChangeToSelection = useCallback((keys: CellKey[], formData: { price: unknown; effectiveDate: unknown }) => {
        apply(e => e.applyPriceChangeToSelection(keys, formData));
    }, [apply]);

    // -------------------------------------------------------------------
    // Merge / unmerge
    // -------------------------------------------------------------------

    const mergeRight = useCallback((rowIndex: number, colIndex: number) => {
        apply(e => e.mergeRight(rowIndex, colIndex));
    }, [apply]);

    const mergeLeft = useCallback((rowIndex: number, colIndex: number) => {
        apply(e => e.mergeLeft(rowIndex, colIndex));
    }, [apply]);

    const unmerge = useCallback((rowIndex: number, colIndex: number) => {
        apply(e => e.unmerge(rowIndex, colIndex));
    }, [apply]);

    const mergeSelected = useCallback((selectionKeys: CellKey[]) => {
        let succeeded = false;
        setEditor(prev => {
            if (!prev) return prev;
            const { editor: next, error } = prev.mergeSelected(selectionKeys);
            if (error) {
                optionsRef.current.onMergeError?.(error);
                return prev;
            }
            if (next !== prev) {
                notifyMutated();
                succeeded = true;
            }
            return next;
        });
        return succeeded;
    }, [notifyMutated]);

    // -------------------------------------------------------------------
    // Resize
    // -------------------------------------------------------------------

    const addColumn = useCallback((maxColumns = 20) => {
        setEditor(prev => {
            if (!prev) return prev;
            const { editor: next, error } = prev.addColumn(maxColumns);
            if (error) {
                optionsRef.current.onLimitError?.(error);
                return prev;
            }
            if (next !== prev) notifyMutated();
            return next;
        });
    }, [notifyMutated]);

    const addRow = useCallback((maxRows = 20) => {
        setEditor(prev => {
            if (!prev) return prev;
            const { editor: next, error } = prev.addRow(maxRows);
            if (error) {
                optionsRef.current.onLimitError?.(error);
                return prev;
            }
            if (next !== prev) notifyMutated();
            return next;
        });
    }, [notifyMutated]);

    const resize = useCallback((newRows: number, newColumns: number) => {
        apply(e => e.resize(newRows, newColumns));
    }, [apply]);

    // -------------------------------------------------------------------
    // Metadata (rename does NOT mark dirty in the original — it's saved
    // immediately via PlanogramService.updateBasicInfo, not part of the
    // draft/save cycle — so it intentionally does not call apply()/notifyMutated.)
    // -------------------------------------------------------------------

    const setData = useCallback((patch: Partial<PlanogramData>) => {
        setEditor(prev => (prev ? PlanogramEditor.from({ ...prev.data, ...patch }) : prev));
    }, []);

    return {
        editor,
        data: editor?.data ?? null,
        load,
        resetToOrigin,
        setData,

        updateCellField,
        deleteCell,
        clearCell,
        clearCells,
        duplicateCell,

        addProduct,
        patchProductDetails,
        applyBulkCellSettings,
        applySingleCellSettings,
        applyPriceChangeToSelection,

        mergeRight,
        mergeLeft,
        unmerge,
        mergeSelected,

        addColumn,
        addRow,
        resize,
    };
}

export type UsePlanogramEditorReturn = ReturnType<typeof usePlanogramEditor>;